"use client";

import Link from "next/link";
import { motion } from "motion/react";
import { useNavigation } from "@/lib/navigation";
import { useSession } from "@/lib/session";
import { usePhone } from "@/lib/device";
import { openLogin } from "@/components/auth/login";
import { TAB_PATHS } from "@/lib/address";
import { TABS } from "@/components/shell/tabs";

/**
 * The phone's tabs along the bottom (tabs.ts): Início, Caminhadas, Comunidade or
 * Favoritos, Os meus eventos. The menu that was the fourth tab is behind the
 * profile icon in the header (PageHeader).
 */

export function PhoneTabBar() {
  const { tab } = useNavigation();
  const { likelySignedIn } = useSession();
  const { phone, ready } = usePhone();

  // mounted on a phone only: hidden with CSS on a desktop, its sliding pill
  // still animated on every navigation over elements with no size
  if (!ready || !phone) {
    return null;
  }

  return (
    // phone-tab-bar is named for the route transitions, and only while one runs
    // (globals.css), so the bar is a layer of its own that stays put over the pages
    // changing under it; unnamed, the page being animated was drawn over it, and
    // what had scrolled under the bar showed through
    <nav
      className="phone-tab-bar fixed inset-x-0 bottom-0 z-40 border-t border-line bg-surface/95 backdrop-blur-md desk:hidden"
      style={{ paddingBottom: "var(--safe-bottom)" }}
      aria-label="Secções"
    >
      <ul className="flex" style={{ height: "var(--tabbar-h)" }}>
        {TABS.map(({ id, label, icon: Icon, account }) => {
          const active = tab === id;
          const needsLogin = account && !likelySignedIn;
          const content = (
            <>
              <span className="relative flex h-7 w-12 items-center justify-center">
                {active && (
                  <motion.span
                    layoutId="tab-pill"
                    className="absolute inset-0 rounded-full bg-brand-soft"
                    transition={{ type: "spring", stiffness: 500, damping: 36 }}
                  />
                )}
                <Icon size={21} strokeWidth={active ? 2.4 : 2} className={`relative ${active ? "text-brand" : "text-ink-soft"}`} />
              </span>
              <span className={`text-[10.5px] font-semibold leading-none ${active ? "text-brand" : "text-ink-soft"}`}>{label}</span>
            </>
          );
          return (
            <li key={id} className="flex-1">
              {needsLogin ? (
                <button type="button" onClick={() => openLogin()} className="flex h-full w-full flex-col items-center justify-center gap-1">
                  {content}
                </button>
              ) : (
                <Link href={TAB_PATHS[id]} className="flex h-full w-full flex-col items-center justify-center gap-1" aria-current={active ? "page" : undefined}>
                  {content}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
